import { useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ProdutoSearchCombobox } from "@/components/ProdutoSearchCombobox";
import type { ClientePrecoProdutoApi, SalvarPrecoProdutoRequest } from "../types";

interface Props {
  precos:    ClientePrecoProdutoApi[];
  onAdd:     (data: SalvarPrecoProdutoRequest) => void;
  onDelete:  (id: number) => void;
  isLoading?: boolean;
}

export function PrecoProdutoTable({ precos, onAdd, onDelete, isLoading }: Props) {
  const [produtoId, setProdutoId] = useState(0);
  const [quantidade, setQuantidade] = useState("");
  const [preco, setPreco] = useState("");
  const [desconto, setDesconto] = useState("");

  const handleAdd = () => {
    if (!produtoId) return;
    onAdd({
      produtoId,
      quantidadeMaiorQue: parseFloat(quantidade) || 0,
      precoVenda:         parseFloat(preco) || 0,
      percDesconto:       parseFloat(desconto) || 0,
    });
    setProdutoId(0); setQuantidade(""); setPreco(""); setDesconto("");
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end border rounded-lg p-3 bg-muted/30">
        <div className="md:col-span-5 space-y-1">
          <Label className="text-xs">Produto</Label>
          <ProdutoSearchCombobox value={produtoId || undefined} onChange={setProdutoId} />
        </div>
        <div className="md:col-span-2 space-y-1">
          <Label className="text-xs">Qtd. maior que</Label>
          <Input type="number" step="0.001" min="0" value={quantidade} onChange={(e) => setQuantidade(e.target.value)} placeholder="0,00" />
        </div>
        <div className="md:col-span-2 space-y-1">
          <Label className="text-xs">R$ Venda</Label>
          <Input type="number" step="0.01" min="0" value={preco} onChange={(e) => setPreco(e.target.value)} placeholder="0,00" />
        </div>
        <div className="md:col-span-2 space-y-1">
          <Label className="text-xs">% Desconto</Label>
          <Input type="number" step="0.01" min="0" max="100" value={desconto} onChange={(e) => setDesconto(e.target.value)} placeholder="0,00" />
        </div>
        <div className="md:col-span-1">
          <Button type="button" onClick={handleAdd} disabled={!produtoId || isLoading} className="w-full" title="Adicionar">
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="border rounded-lg overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-24">Código</TableHead>
              <TableHead>Descrição</TableHead>
              <TableHead className="text-right">Qtd. maior que</TableHead>
              <TableHead className="text-right">R$ Venda</TableHead>
              <TableHead className="text-right">% Desconto</TableHead>
              <TableHead className="w-12" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {precos.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-muted-foreground py-6 text-sm">
                  Nenhum preço diferenciado. Selecione um produto acima e clique em +.
                </TableCell>
              </TableRow>
            )}
            {precos.map((p) => (
              <TableRow key={p.id}>
                <TableCell className="font-mono text-sm">{p.produtoCodigo}</TableCell>
                <TableCell className="text-sm">{p.produtoDescricao}</TableCell>
                <TableCell className="text-right text-sm">{p.quantidadeMaiorQue.toFixed(3)}</TableCell>
                <TableCell className="text-right text-sm">{p.precoVenda.toFixed(2)}</TableCell>
                <TableCell className="text-right text-sm">{p.percDesconto.toFixed(2)}%</TableCell>
                <TableCell>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => onDelete(p.id)}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
